import React, { useEffect, useRef, useState } from 'react'
import { HiX } from 'react-icons/hi'

const EXIT_MS = 200

// Editor panel that slides in from the right (a bottom sheet on phones) over the page, so the page behind never
// moves. Closes on Escape / overlay click unless `busy`; keeps its last content while it slides out.
const SidePanel = ({ open, title, children, footer, onClose, busy = false }) => {
    const [mounted, setMounted] = useState(open)
    const [closing, setClosing] = useState(false)
    const last = useRef({ title, children, footer })
    const panelRef = useRef(null)
    const closeRef = useRef(onClose)
    const busyRef = useRef(busy)
    closeRef.current = onClose
    busyRef.current = busy
    if (open) last.current = { title, children, footer }

    useEffect(() => {
        if (open) {
            setMounted(true)
            setClosing(false)
            return undefined
        }
        if (!mounted) return undefined
        setClosing(true)
        const timer = setTimeout(() => { setMounted(false); setClosing(false) }, EXIT_MS)
        return () => clearTimeout(timer)
    }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

    // lock the page scroll while open; if the page had a scrollbar, its space is kept so nothing shifts sideways
    useEffect(() => {
        if (!mounted) return undefined
        const root = document.documentElement
        const returnFocus = document.activeElement
        const hadScrollbar = window.innerWidth > root.clientWidth
        root.classList.add('mv-scroll-locked')
        if (hadScrollbar) root.classList.add('mv-scroll-gutter')
        const onKey = (e) => { if (e.key === 'Escape' && !busyRef.current) closeRef.current() }
        document.addEventListener('keydown', onKey)
        if (panelRef.current && !panelRef.current.contains(document.activeElement)) panelRef.current.focus({ preventScroll: true })
        return () => {
            root.classList.remove('mv-scroll-locked', 'mv-scroll-gutter')
            document.removeEventListener('keydown', onKey)
            if (returnFocus && returnFocus.focus && document.contains(returnFocus)) returnFocus.focus({ preventScroll: true })
        }
    }, [mounted])

    if (!mounted) return null
    const view = open ? { title, children, footer } : last.current

    return (
        <div className={'mv-panel-overlay' + (closing ? ' is-closing' : '')} onClick={() => !busy && onClose()}>
            <aside className='mv-panel' role='dialog' aria-modal='true' aria-labelledby='mv-panel-title'
                tabIndex={-1} ref={panelRef} onClick={(e) => e.stopPropagation()}>
                <header className='mv-panel-head'>
                    <h2 id='mv-panel-title' className='mv-panel-title'>{view.title}</h2>
                    <button type='button' className='mv-icon-btn' aria-label='Close' onClick={onClose} disabled={busy}>
                        <HiX />
                    </button>
                </header>
                <div className='mv-panel-body'>{view.children}</div>
                {view.footer && <footer className='mv-panel-foot'>{view.footer}</footer>}
            </aside>
        </div>
    )
}

export default SidePanel
